import {
  onlineManager,
  type Query,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import type {
  PersistQueryClientOptions,
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client';
import { clear, createStore, del, get, set, type UseStore } from 'idb-keyval';

import { API_BASE } from '@/shared/api/client';
import { useAuthStore } from '@/shared/stores/auth.store';

/**
 * Offline cache for the phone (docs/release-1/17-V2-BUILD-PLAN.md §4 W3-E):
 * a cold launch of the installed app shows My Work from IndexedDB at once and
 * revalidates in the background; in airplane mode the last good data stays.
 *
 * Privacy (risk #11 "offline cache on a shared device", Appendix B):
 *  - ALLOWLIST ONLY: `['me','work',…]`, `['me','inbox',…]`, `['me','attention']`,
 *    and (W4) my prefs + saved views `['me','prefs',…]`, Home `['me','home',…]`,
 *    `['me','projects']` and `['me','unread-summary']` — so Home, the Projects
 *    tab and the badges render on a cold or offline start.
 *    Nothing else is ever written — no team lists, no task bodies, no private
 *    task panel (`['me','mine',…]`), no mutations.
 *  - Bound to the signed-in user: one entry per user id, read and written only
 *    while that user is signed in.
 *  - Wiped on sign-out (and on a session that ends or switches user) via
 *    `useAuthStore.subscribe` — on disk and in memory, so neither the cached
 *    copy nor anything else that session loaded outlives it.
 *  - Busted by every new build, so an old shape never reaches new code.
 */

/** Query-key prefixes that may be persisted. Keep in sync with the doc comment above. */
export const PERSISTED_QUERY_PREFIXES: ReadonlyArray<readonly string[]> = [
  ['me', 'work'],
  ['me', 'inbox'],
  ['me', 'attention'],
  ['me', 'prefs'],
  ['me', 'home'],
  ['me', 'projects'],
  ['me', 'unread-summary'],
];

/** How long a persisted snapshot (and the in-memory copy it feeds) is trusted. */
export const OFFLINE_CACHE_MAX_AGE = 3 * 24 * 60 * 60 * 1000;

const SCHEMA_VERSION = 1;
const THROTTLE_MS = 1000;
/** A hung IndexedDB open (seen on iOS) must never hold the app's queries back. */
const RESTORE_TIMEOUT_MS = 1500;
const PROBE_INTERVAL_MS = 20_000;

/**
 * Busts the cache on every deploy: an explicit `VITE_APP_VERSION` when the
 * build sets one, otherwise this module's own content-hashed chunk URL
 * (`/assets/index-<hash>.js` in production), which changes with every build.
 */
export const OFFLINE_CACHE_BUSTER = `${SCHEMA_VERSION}:${
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? new URL(import.meta.url).pathname
}`;

export function isPersistedQueryKey(key: QueryKey): boolean {
  return PERSISTED_QUERY_PREFIXES.some((prefix) => prefix.every((part, i) => key[i] === part));
}

/** Only allowlisted queries that hold data (a failed refetch keeps its last good data). */
export function shouldPersistQuery(query: Query): boolean {
  return isPersistedQueryKey(query.queryKey) && query.state.data !== undefined;
}

/**
 * What is written: the allowlisted queries as their last good data. A query
 * whose latest refetch failed (captive portal, server hiccup) is stored as a
 * success so the next cold launch shows the data, not an error.
 */
export function toPersistable(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      mutations: [],
      queries: client.clientState.queries
        .filter((q) => isPersistedQueryKey(q.queryKey) && q.state.data !== undefined)
        .map((q) => ({
          ...q,
          state: {
            ...q.state,
            status: 'success' as const,
            fetchStatus: 'idle' as const,
            error: null,
            fetchFailureCount: 0,
            fetchFailureReason: null,
          },
        })),
    },
  };
}

// ── Storage ─────────────────────────────────────────────────────────────────

/** The few key/value calls the persister needs — IndexedDB in the app, a Map in tests. */
export interface OfflineStorage {
  get: (key: string) => Promise<unknown>;
  set: (key: string, value: unknown) => Promise<void>;
  del: (key: string) => Promise<void>;
  clear: () => Promise<void>;
}

let idbStore: UseStore | undefined;
/** A database of its own, so a sign-out wipe can never touch anything else. */
function store(): UseStore {
  idbStore ??= createStore('projoint-offline', 'query-cache');
  return idbStore;
}

export const idbStorage: OfflineStorage = {
  get: (key) => get(key, store()),
  set: (key, value) => set(key, value, store()),
  del: (key) => del(key, store()),
  clear: () => clear(store()),
};

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(undefined);
      },
    );
  });
}

// ── Persister ───────────────────────────────────────────────────────────────

export interface OfflinePersister extends Persister {
  /** Write the pending snapshot now (the app is being backgrounded). */
  flush: () => Promise<void>;
  /** Drop every user's snapshot and any pending write (sign-out). */
  purge: () => Promise<void>;
}

export interface OfflinePersisterOptions {
  storage?: OfflineStorage;
  /** The signed-in user, read at every call — no user, no reads or writes. */
  getUserId?: () => string | null;
  throttleMs?: number;
  restoreTimeoutMs?: number;
}

function currentUserId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

export function createOfflinePersister({
  storage = idbStorage,
  getUserId = currentUserId,
  throttleMs = THROTTLE_MS,
  restoreTimeoutMs = RESTORE_TIMEOUT_MS,
}: OfflinePersisterOptions = {}): OfflinePersister {
  const keyFor = (userId: string) => `rq:${userId}`;
  let pending: { userId: string; client: PersistedClient } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Every cache event (any query) triggers a save; skip the write when none of
  // the allowlisted queries changed since the last one.
  let lastWritten = '';
  // Bumped by every purge: a read that was in flight across a sign-out is dropped.
  let generation = 0;

  const flush = async () => {
    if (timer) clearTimeout(timer);
    timer = null;
    const next = pending;
    pending = null;
    // Re-check the user at write time: a sign-out in between drops the write.
    if (!next || getUserId() !== next.userId) return;
    const snapshot = toPersistable(next.client);
    const signature = `${next.userId}|${snapshot.clientState.queries
      .map((q) => `${q.queryHash}@${q.state.dataUpdatedAt}`)
      .join(',')}`;
    if (signature === lastWritten) return;
    try {
      await storage.set(keyFor(next.userId), snapshot);
      lastWritten = signature;
    } catch {
      /* quota / private mode — the cache is best-effort */
    }
  };

  return {
    persistClient: (client) => {
      const userId = getUserId();
      if (!userId) return;
      pending = { userId, client };
      timer ??= setTimeout(() => void flush(), throttleMs);
    },
    restoreClient: async () => {
      const userId = getUserId();
      if (!userId) return undefined;
      const started = generation;
      const saved = await withTimeout(storage.get(keyFor(userId)), restoreTimeoutMs);
      // The session ended (or switched) while IndexedDB was answering.
      if (started !== generation || getUserId() !== userId) return undefined;
      return (saved ?? undefined) as PersistedClient | undefined;
    },
    removeClient: async () => {
      const userId = getUserId();
      lastWritten = '';
      if (!userId) return;
      try {
        await storage.del(keyFor(userId));
      } catch {
        /* best-effort */
      }
    },
    flush,
    purge: async () => {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
      lastWritten = '';
      generation += 1;
      try {
        await storage.clear();
      } catch {
        /* best-effort */
      }
    },
  };
}

// ── Sign-out wipe ───────────────────────────────────────────────────────────

/**
 * When a session ends — sign-out, a dead session, a sign-out in another tab —
 * or another user signs in over it, wipe the offline copy on disk AND
 * everything that session cached in memory, so nothing of user A reaches
 * whoever uses this tab next. Uses the store's subscription, so `auth.store`
 * stays untouched. A profile refresh (same user) changes nothing.
 * Returns the unsubscribe.
 */
export function purgeOnSignOut(queryClient: QueryClient, persister: OfflinePersister): () => void {
  return useAuthStore.subscribe((state, prev) => {
    const before = prev.user?.id ?? null;
    const after = state.user?.id ?? null;
    if (!before || before === after) return;
    void persister.purge();
    queryClient.clear();
  });
}

// ── Online state ────────────────────────────────────────────────────────────

/**
 * React Query only learns about connectivity from `online` / `offline`
 * events, so an app launched in airplane mode would think it is online —
 * firing requests that fail (error toasts, rolled-back edits) instead of
 * pausing them. Trust `navigator.onLine === false` at launch, and while
 * offline probe the API now and then, so a browser that wrongly reports
 * "offline" heals itself instead of staying paused.
 */
let stopOnlineSync: (() => void) | null = null;

export function setupOnlineSync(probeUrl = `${API_BASE}/health/live`): () => void {
  stopOnlineSync?.(); // one probe loop per app
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    onlineManager.setOnline(false);
  }
  let timer: ReturnType<typeof setInterval> | null = null;
  const probe = async () => {
    if (onlineManager.isOnline()) return;
    try {
      const res = await fetch(probeUrl, { cache: 'no-store', credentials: 'omit' });
      if (res.ok) onlineManager.setOnline(true);
    } catch {
      /* still offline */
    }
  };
  const sync = (online: boolean) => {
    if (online && timer) {
      clearInterval(timer);
      timer = null;
    } else if (!online && !timer) {
      timer = setInterval(() => void probe(), PROBE_INTERVAL_MS);
    }
  };
  sync(onlineManager.isOnline());
  const unsubscribe = onlineManager.subscribe(sync);
  stopOnlineSync = () => {
    unsubscribe();
    if (timer) clearInterval(timer);
    stopOnlineSync = null;
  };
  return stopOnlineSync;
}

// ── Wiring ──────────────────────────────────────────────────────────────────

/**
 * Configure `queryClient` for the offline cache and return the options for
 * `<PersistQueryClientProvider persistOptions={…}>` (main.tsx).
 *
 * The allowlisted queries are `offlineFirst` (one try, then wait for the
 * network instead of failing) and stay in memory as long as their snapshot is
 * trusted. Mutations keep the default `online` mode: made offline, they pause
 * and run when the connection returns (React Query resumes them).
 */
export function setupOfflineCache(
  queryClient: QueryClient,
  persister: OfflinePersister = createOfflinePersister(),
): Omit<PersistQueryClientOptions, 'queryClient'> {
  for (const prefix of PERSISTED_QUERY_PREFIXES) {
    queryClient.setQueryDefaults([...prefix], {
      gcTime: OFFLINE_CACHE_MAX_AGE,
      networkMode: 'offlineFirst',
    });
  }
  purgeOnSignOut(queryClient, persister);
  setupOnlineSync();
  if (typeof document !== 'undefined') {
    // iOS may kill a backgrounded app without warning — don't lose the last write.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void persister.flush();
    });
  }
  return {
    persister,
    maxAge: OFFLINE_CACHE_MAX_AGE,
    buster: OFFLINE_CACHE_BUSTER,
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQuery,
      shouldDehydrateMutation: () => false,
    },
  };
}
